import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Truck, Shield, Lock, Eye, Database, Mail, Clock, UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useRegion } from '@/context/RegionContext';

const PoliticaPrivacidade = () => {
  const navigate = useNavigate();
  const { config } = useRegion();
  
  const isPortugal = config.code === 'PT';
  
  // Termos específicos por região
  const terms = {
    documentoFiscal: isPortugal ? 'NIF' : 'CNPJ',
    telefone: isPortugal ? 'telemóvel' : 'celular',
    legislacao: isPortugal ? 'Regulamento Geral sobre a Proteção de Dados (RGPD)' : 'Lei Geral de Proteção de Dados (LGPD)',
    entidade: isPortugal ? 'Comissão Nacional de Proteção de Dados (CNPD)' : 'Autoridade Nacional de Proteção de Dados (ANPD)',
    pais: isPortugal ? 'Portugal' : 'Brasil'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-rota017.png" alt="Rota 017" className="w-10 h-10 rounded-xl object-cover" />
            <span className="text-xl font-bold text-gray-900">Rota 017</span>
          </Link>
          
          <Button variant="outline" onClick={() => navigate('/login')}>
            Entrar
          </Button>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Voltar */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-gray-500 hover:text-orange-600 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar ao início
        </Link>
        
        {/* Título */}
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center">
              <Shield className="w-8 h-8 text-orange-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Política de Privacidade</h1>
              <p className="text-gray-500">{config.flag} {config.name} • Última atualização: Março 2026</p>
            </div>
          </div>
        </div>
        
        {/* Conteúdo */}
        <div className="bg-white rounded-2xl shadow-sm border p-8 space-y-8">
          
          {/* Introdução */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5 text-orange-500" />
              1. Introdução
            </h2>
            <p className="text-gray-600 leading-relaxed">
              A <strong>Rota 017</strong> valoriza a privacidade e a proteção dos dados pessoais dos seus utilizadores. 
              Esta Política de Privacidade descreve como recolhemos, utilizamos, armazenamos e protegemos as suas informações 
              pessoais quando utiliza a nossa plataforma de gestão de entregas B2B.
            </p>
            <p className="text-gray-600 leading-relaxed mt-3">
              Esta política está em conformidade com a <strong>{terms.legislacao}</strong> e aplica-se a todos os 
              utilizadores da plataforma em {terms.pais}.
            </p>
          </section>
          
          {/* Dados Recolhidos */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-orange-500" />
              2. Dados que Recolhemos
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Para fornecer os nossos serviços, recolhemos os seguintes tipos de dados:
            </p>
            
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Dados de Identificação Empresarial</h3>
                <ul className="text-gray-600 space-y-1 list-disc list-inside">
                  <li>{terms.documentoFiscal} da empresa</li>
                  <li>Razão social e nome fantasia</li>
                  <li>Endereço comercial</li>
                  <li>E-mail de contacto</li>
                </ul>
              </div>
              
              <div className="bg-slate-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Dados de Contacto</h3>
                <ul className="text-gray-600 space-y-1 list-disc list-inside">
                  <li>Número de {terms.telefone} (WhatsApp)</li>
                  <li>Endereço de e-mail</li>
                </ul>
              </div>
              
              <div className="bg-slate-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Dados de Localização</h3>
                <ul className="text-gray-600 space-y-1 list-disc list-inside">
                  <li>Endereços de origem e destino das entregas</li>
                  <li>Coordenadas geográficas para cálculo de rotas</li>
                </ul>
              </div>
              
              <div className="bg-slate-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Dados Documentais</h3>
                <ul className="text-gray-600 space-y-1 list-disc list-inside">
                  <li>Cartão {terms.documentoFiscal}</li>
                  <li>Contrato social</li>
                  <li>Documentos de identificação dos sócios</li>
                  <li>CNH (para {isPortugal ? 'estafetas' : 'entregadores'})</li>
                </ul>
              </div>
            </div>
          </section>
          
          {/* Finalidade */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5 text-orange-500" />
              3. Finalidade do Tratamento
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Utilizamos os seus dados pessoais para as seguintes finalidades:
            </p>
            <ul className="text-gray-600 space-y-2 list-disc list-inside">
              <li>Criação e gestão da sua conta na plataforma</li>
              <li>Validação cadastral e verificação de documentos</li>
              <li>Processamento e gestão de entregas</li>
              <li>Comunicação sobre o estado das entregas</li>
              <li>Processamento de pagamentos e faturação</li>
              <li>Suporte ao cliente</li>
              <li>Cumprimento de obrigações legais e fiscais</li>
              <li>Melhoria dos nossos serviços</li>
            </ul>
          </section>
          
          {/* Base Legal */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-orange-500" />
              4. Base Legal para o Tratamento
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              O tratamento dos seus dados pessoais baseia-se nas seguintes bases legais:
            </p>
            <ul className="text-gray-600 space-y-2 list-disc list-inside">
              <li><strong>Execução de contrato:</strong> Para prestar os serviços contratados</li>
              <li><strong>Consentimento:</strong> Para comunicações de marketing (quando aplicável)</li>
              <li><strong>Obrigação legal:</strong> Para cumprimento de obrigações fiscais e regulatórias</li>
              <li><strong>Interesse legítimo:</strong> Para melhoria dos serviços e prevenção de fraudes</li>
            </ul>
          </section>
          
          {/* Partilha de Dados */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">5. Partilha de Dados</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Os seus dados podem ser partilhados com:
            </p>
            <ul className="text-gray-600 space-y-2 list-disc list-inside">
              <li><strong>{isPortugal ? 'Estafetas' : 'Entregadores'}:</strong> Endereços de entrega necessários para a execução do serviço</li>
              <li><strong>Clientes:</strong> Informações do {isPortugal ? 'estafeta' : 'entregador'} responsável pela entrega</li>
              <li><strong>Processadores de pagamento:</strong> Dados necessários para processar transações</li>
              <li><strong>Autoridades:</strong> Quando exigido por lei ou ordem judicial</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-4">
              <strong>Não vendemos</strong> os seus dados pessoais a terceiros.
            </p>
          </section>
          
          {/* Retenção */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-500" />
              6. Período de Retenção
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Os seus dados são mantidos enquanto a sua conta estiver ativa e por um período adicional necessário 
              para cumprir obrigações legais, resolver disputas e fazer cumprir os nossos acordos. 
              Dados fiscais são mantidos pelo período legalmente exigido ({isPortugal ? '10 anos' : '5 anos'}).
            </p>
          </section>
          
          {/* Direitos */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">7. Os Seus Direitos</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Ao abrigo da {terms.legislacao}, tem os seguintes direitos:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                <h3 className="font-semibold text-orange-900 mb-1">Direito de Acesso</h3>
                <p className="text-sm text-orange-700">Solicitar uma cópia dos seus dados pessoais</p>
              </div>
              <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                <h3 className="font-semibold text-orange-900 mb-1">Direito de Retificação</h3>
                <p className="text-sm text-orange-700">Corrigir dados incorretos ou desatualizados</p>
              </div>
              <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                <h3 className="font-semibold text-orange-900 mb-1">Direito ao Apagamento</h3>
                <p className="text-sm text-orange-700">Solicitar a eliminação dos seus dados</p>
              </div>
              <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                <h3 className="font-semibold text-orange-900 mb-1">Direito à Portabilidade</h3>
                <p className="text-sm text-orange-700">Receber os seus dados em formato estruturado</p>
              </div>
              <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                <h3 className="font-semibold text-orange-900 mb-1">Direito de Oposição</h3>
                <p className="text-sm text-orange-700">Opor-se ao tratamento dos seus dados</p>
              </div>
              <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                <h3 className="font-semibold text-orange-900 mb-1">Direito de Limitação</h3>
                <p className="text-sm text-orange-700">Restringir o tratamento dos seus dados</p>
              </div>
            </div>
            <p className="text-gray-600 leading-relaxed mt-4">
              Para exercer qualquer destes direitos, contacte-nos através da página de <Link to="/suporte" className="text-orange-600 hover:underline">Suporte</Link>.
            </p>
          </section>
          
          {/* Segurança */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">8. Segurança dos Dados</h2>
            <p className="text-gray-600 leading-relaxed">
              Implementamos medidas técnicas e organizacionais adequadas para proteger os seus dados, incluindo:
            </p>
            <ul className="text-gray-600 space-y-2 list-disc list-inside mt-3">
              <li>Encriptação de dados em trânsito e em repouso</li>
              <li>Acesso restrito com autenticação segura</li>
              <li>Monitorização contínua de segurança</li>
              <li>Backups regulares</li>
              <li>Formação da equipa em proteção de dados</li>
            </ul>
          </section>
          
          {/* Cookies */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">9. Cookies</h2>
            <p className="text-gray-600 leading-relaxed">
              Utilizamos cookies essenciais para o funcionamento da plataforma, incluindo:
            </p>
            <ul className="text-gray-600 space-y-2 list-disc list-inside mt-3">
              <li><strong>Cookies de sessão:</strong> Para manter a sua sessão ativa</li>
              <li><strong>Cookies de preferências:</strong> Para guardar as suas preferências (ex: região selecionada)</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-3">
              Não utilizamos cookies de rastreamento ou publicidade de terceiros.
            </p>
          </section>
          
          {/* Contacto */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-orange-500" />
              10. Contacto
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Para questões sobre esta política ou sobre o tratamento dos seus dados pessoais, contacte-nos através:
            </p>
            <div className="bg-slate-50 rounded-xl p-4 mt-4">
              <p className="text-gray-700">
                <strong>Rota 017 - Proteção de Dados</strong><br />
                Site: <a href="https://www.rota017.com.br" className="text-orange-600 hover:underline">www.rota017.com.br</a><br />
                E-mail: <a href="mailto:contato@rota017.com.br" className="text-orange-600 hover:underline">contato@rota017.com.br</a><br />
                Página de Suporte: <Link to="/suporte" className="text-orange-600 hover:underline">/suporte</Link>
              </p>
            </div>
            {isPortugal && (
              <p className="text-gray-600 leading-relaxed mt-4">
                Tem também o direito de apresentar uma reclamação à <strong>{terms.entidade}</strong> se considerar 
                que o tratamento dos seus dados viola os seus direitos.
              </p>
            )}
          </section>
          
          {/* Alterações */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">11. Alterações a Esta Política</h2>
            <p className="text-gray-600 leading-relaxed">
              Podemos atualizar esta Política de Privacidade periodicamente. Quaisquer alterações significativas 
              serão comunicadas através da plataforma ou por e-mail. A data da última atualização está indicada 
              no topo deste documento.
            </p>
          </section>
          
        </div>
        
        {/* Footer da página */}
        <div className="mt-8 text-center">
          <Button 
            onClick={() => navigate('/')}
            className="bg-orange-600 hover:bg-orange-700"
          >
            Voltar ao Início
          </Button>
        </div>
      </main>
    </div>
  );
};

export default PoliticaPrivacidade;
